import { Component, effect, EventEmitter, inject, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { Product, PurchaseOrder } from 'depoto-core/src/entities'
import { CoreService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'
import type { WorkBook, WorkSheet } from 'xlsx'
import { asyncForEach, buildImportColumnMap, SchemaUtil, UI_langs, fileTemplatesURL, loadXlsx } from '../../../utils'
import { DepotService } from '../../../services/entity-services/depot.service'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { SupplierService } from '../../../services/entity-services/supplier.services'
import { PaymentService } from '../../../services/entity-services/payment.service'
import { FmtDatePipe } from '../../../pipes'

type ProductSearchField = 'ean' | 'code'

type PurchaseOrderImportError = {
  orderIdentifier?: string
  rowNumber?: string
  message: string
  params?: Record<string, string>
}

const EMPTY_IMPORT_MAP = {
  supplierReference: -1,
  ean: -1,
  code: -1,
  productName: -1,
  quantity: -1,
  purchasePriceWithoutVat: -1,
  currency: -1,
  depot: -1,
  supplier: -1,
  reference: -1,
  dateEstimatedArrival: -1,
  note: -1,
  privateNote: -1,
  itemNote: -1,
}

@Component({
  selector: 'modal-purchase-orders-import',
  templateUrl: './modal-purchase-orders-import.component.html',
  styleUrl: './modal-purchase-orders-import.component.scss',
  providers: [FmtDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalPurchaseOrdersImportComponent {
  private depotService = inject(DepotService)
  private supplierService = inject(SupplierService)
  private paymentService = inject(PaymentService)
  @ViewChild('modal') public childModal: ModalContainerComponent
  @Output() public onPurchaseOrdersImported: EventEmitter<Array<PurchaseOrder>> = new EventEmitter()
  loadedFileEv: any
  purchaseOrders: PurchaseOrder[] = []
  fileTypes: string[] = ['depoto-purchase-orders.xlsx']
  selectedFileType = 'depoto-purchase-orders.xlsx'
  isImportFileLoaded = false
  templatesURL = fileTemplatesURL
  progress = 0
  loading = false
  defaultColumnTypesCS = {
    supplierReference: 'Reference dodavatele',
    ean: 'EAN produktu',
    code: 'SKU produktu',
    productName: 'Název produktu',
    quantity: 'Množství',
    purchasePriceWithoutVat: 'Nákupní cena bez DPH',
    currency: 'Měna',
    depot: 'Sklad', // Název skladu
    supplier: 'Dodavatel',
    reference: 'Reference',
    dateEstimatedArrival: 'Datum očekávaného příjmu',
    note: 'Poznámka objednávky',
    privateNote: 'Privátní poznámka',
    itemNote: 'Poznámka položky',
  }
  defaultColumnTypesEN = {
    supplierReference: 'Supplier reference',
    ean: 'Product EAN',
    code: 'Product SKU',
    productName: 'Product name',
    quantity: 'Quantity',
    purchasePriceWithoutVat: 'Purchase price without VAT',
    currency: 'Currency',
    depot: 'Depot name',
    supplier: 'Supplier name',
    reference: 'Reference',
    dateEstimatedArrival: 'Date of expected receipt',
    note: 'Note for order',
    privateNote: 'Private note',
    itemNote: 'Note for item',
  }
  columnTypes: Record<string, unknown>[] = []
  importMap: any = { ...EMPTY_IMPORT_MAP }
  isImportMapSet = false
  isImportMapUpdating = false
  worksheetNames: string[] = []
  selectedWorksheetName = ''
  uintArray: Uint8Array | null = null
  errors: PurchaseOrderImportError[] = []

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  constructor(
    private core: CoreService,
    private translateService: TranslateService,
    private readonly fmtDatePipe: FmtDatePipe
  ) {
    effect(() => {
      this.queryCurrencies.data()
      this.queryDepotsOptions.data()
      this.querySuppliers.data()
    })
  }

  showChildModal(): void {
    this.loadedFileEv = null
    this.isImportFileLoaded = false
    this.purchaseOrders = []
    this.errors = []
    this.isImportMapSet = false
    this.isImportMapUpdating = false
    this.worksheetNames = []
    this.selectedWorksheetName = ''
    this.columnTypes = []
    this.importMap = { ...EMPTY_IMPORT_MAP }
    this.uintArray = null
    this.childModal.showChildModal()
  }

  hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  loadFile(event): void {
    this.loadedFileEv = event
  }

  async handleImportFile(): Promise<PurchaseOrder[]> {
    if (!this.loadedFileEv) {
      alert(this.translateService.instant('modal-purchase-orders-import.no-file'))
      return
    }
    this.loading = true
    this.errors = []
    let arrayBuff = null
    const isXls = this.loadedFileEv.target.value.endsWith('.xlsx')
    if (!isXls || this.selectedFileType != 'depoto-purchase-orders.xlsx') {
      alert(this.translateService.instant('modal-orders-import.wrong-format'))
      this.loading = false
      return
    }
    try {
      arrayBuff = await this.getDataFromImportedFile(this.loadedFileEv)
      this.uintArray = new Uint8Array(arrayBuff)
      if (isXls) {
        this.purchaseOrders = await this.parseXlsDepoto(this.uintArray)
      }
    } catch (err) {
      console.warn(err)
      alert(
        typeof err === 'string'
          ? err
          : err.message
            ? err.message === "Cannot read property 'childNodes' of undefined"
              ? this.translateService.instant('modal-purchase-orders-import.import.error')
              : err.message
            : JSON.stringify(err)
      )
    } finally {
      this.loading = false
    }
    if (this.purchaseOrders.length > 0 && !this.purchaseOrders[0].supplierReference) {
      alert(this.translateService.instant('modal-purchase-orders-import.wrong-format'))
    }
  }

  getDataFromImportedFile(inputEvent: Event): Promise<string | any> {
    const target = inputEvent.target as HTMLInputElement
    return new Promise((resolve, reject) => {
      const file = target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.readAsArrayBuffer(file)
        reader.onload = evt => {
          resolve(evt.target.result)
        }
        reader.onerror = () => {
          reject(this.translateService.instant('file-processing-error', { name: file.name }))
        }
      } else {
        reject(this.translateService.instant('no-file'))
      }
    })
  }

  async setImportMapAndContinue() {
    this.loading = true
    this.isImportMapUpdating = false
    this.isImportMapSet = true
    this.errors = []
    try {
      this.purchaseOrders = await this.parseXlsDepoto(this.uintArray)
      this.isImportFileLoaded = true
    } catch (err) {
      this.errors.push({
        message: this.getErrorMessages(err).join('. ') || 'modal-purchase-orders-import.import.error',
      })
      this.isImportMapUpdating = true
      this.isImportMapSet = false
      this.isImportFileLoaded = false
    } finally {
      this.loading = false
    }
  }

  async selectWorksheet(sheetName: string): Promise<void> {
    if (!this.uintArray || sheetName === this.selectedWorksheetName) {
      return
    }

    this.selectedWorksheetName = sheetName
    this.purchaseOrders = []
    this.errors = []
    this.progress = 0
    this.isImportFileLoaded = false
    this.isImportMapSet = false
    this.isImportMapUpdating = false
    this.columnTypes = []
    this.importMap = { ...EMPTY_IMPORT_MAP }
    this.loading = true

    try {
      await this.parseXlsDepoto(this.uintArray)
    } catch (err) {
      this.errors.push({
        message: this.getErrorMessages(err).join('. ') || 'modal-purchase-orders-import.import.error',
      })
      this.isImportMapUpdating = true
    } finally {
      this.loading = false
    }
  }

  async import() {
    const results = []
    this.progress = 0
    this.loading = true
    let x = 1
    for (const o of this.purchaseOrders) {
      try {
        const res = await this.core.services.purchase.create(o)
        results.push(res)
      } catch (e) {
        this.errors.push({
          orderIdentifier: this.castImportCellToString(o.supplierReference || o.externalReference).trim(),
          message: e.message,
        })
        console.error(e)
      }
      this.progress = x / (this.purchaseOrders.length / 100)
      x++
    }
    this.loading = false
    if (this.errors.length === 0) {
      this.loadedFileEv = null
      this.purchaseOrders = []
      this.isImportMapSet = false
      this.isImportMapUpdating = false
      this.hideChildModal()
    }
    this.onPurchaseOrdersImported.emit(this.purchaseOrders)
    this.isImportFileLoaded = false
  }

  private getErrorMessages(error: any): string[] {
    if (!error) {
      return []
    }
    if (Array.isArray(error)) {
      return error.flatMap(e => this.getErrorMessages(e))
    }
    if (typeof error === 'string') {
      return [error]
    }
    if (error.message) {
      return [error.message]
    }
    if (error.errorMessage) {
      return [error.errorMessage]
    }
    try {
      return [JSON.stringify(error)]
    } catch {
      return [`${error}`]
    }
  }

  private findImportProduct(field: ProductSearchField, value: unknown): Promise<Product | null> {
    const normalizedValue = this.castImportCellToString(value).trim()
    if (!normalizedValue) {
      return Promise.resolve(null)
    }
    return this.core.services.product
      .getList(
        { filters: { [field]: normalizedValue } },
        { ...SchemaUtil.productImportSearch, purchaseCurrency: { id: null } }
      )
      .then(result => result?.items?.[0] ?? null)
      .catch(() => null)
  }

  private async findImportProductForRow(
    importMap: typeof EMPTY_IMPORT_MAP,
    row: Array<unknown>
  ): Promise<{ product: Product | null; identifier: string }> {
    const ean = importMap.ean >= 0 ? this.castImportCellToString(row[importMap.ean]).trim() : ''
    const code = importMap.code >= 0 ? this.castImportCellToString(row[importMap.code]).trim() : ''

    if (ean) {
      const product = await this.findImportProduct('ean', ean)
      if (product) {
        return { product, identifier: ean }
      }
    }

    if (code) {
      const product = await this.findImportProduct('code', code)
      if (product) {
        return { product, identifier: code }
      }
    }

    return { product: null, identifier: code || ean }
  }

  private castImportCellToString(value: unknown): string {
    return typeof value === 'string' ? value : typeof value === 'number' ? value.toFixed(0) : value ? `${value}` : ''
  }

  private getImportErrorContext(
    importMap: typeof EMPTY_IMPORT_MAP,
    row: Array<unknown>,
    rowIndex: number
  ): Pick<PurchaseOrderImportError, 'orderIdentifier' | 'rowNumber'> {
    const supplierReference = this.castImportCellToString(row[importMap.supplierReference]).trim()
    const externalReference = this.castImportCellToString(row[importMap.reference]).trim()
    const orderIdentifier = supplierReference || externalReference

    return orderIdentifier ? { orderIdentifier } : { rowNumber: `${rowIndex + 1}` }
  }

  protected async parseXlsDepoto(typedArray: Uint8Array): Promise<Array<PurchaseOrder>> {
    const XLSX = await loadXlsx()
    const wb: WorkBook = XLSX.read(typedArray, { cellDates: true })
    const purchaseOrders: any = {}
    const parsedPurchaseOrders: PurchaseOrder[] = []
    this.progress = 0

    this.worksheetNames = [...wb.SheetNames]
    if (!this.selectedWorksheetName || !wb.Sheets[this.selectedWorksheetName]) {
      this.selectedWorksheetName = wb.Sheets.depoto_import ? 'depoto_import' : wb.SheetNames[0] ?? ''
    }
    const ws: WorkSheet | undefined = wb.Sheets[this.selectedWorksheetName]
    if (!ws) {
      throw new Error(this.translateService.instant('modal-purchase-orders-import.import.error'))
    }

    const data: Array<Array<any>> | any = XLSX.utils.sheet_to_json(ws, { header: 1 })
    await asyncForEach(data, async (n, i) => {
      if (n?.length > 0) {
        if (i === 0 && !this.isImportMapSet) {
          const headings = n.map(c => this.castImportCellToString(c))
          this.columnTypes = [{ id: 'none', name: ' ' }, ...headings.map((h, index) => ({ id: index, name: h }))]
          const defaultColumnsByLang = UI_langs.map(lang => this['defaultColumnTypes' + lang])
          this.importMap = buildImportColumnMap(headings, defaultColumnsByLang)
          this.isImportMapUpdating = true
          return
        } else if (i > 0 && this.isImportMapSet) {
          const importMap = this.importMap
          const rowNumber = `${n[importMap.supplierReference]}`
          const errorContext = this.getImportErrorContext(importMap, n, i)

          if (!this.checkDepotIsValid(n[importMap.depot])) {
            this.errors.push({
              ...errorContext,
              message: 'modal-purchase-orders-import.missing-depot',
            })
            return
          } else if (!this.checkSupplierIsValid(n[importMap.supplier])) {
            this.errors.push({
              ...errorContext,
              message: 'modal-purchase-orders-import.missing-supplier',
            })
            return
          }

          const productMatch = await this.findImportProductForRow(importMap, n)
          if (productMatch.product) {
            if (!purchaseOrders[rowNumber]) {
              purchaseOrders[rowNumber] = new PurchaseOrder()
            }
            purchaseOrders[rowNumber].supplierReference = rowNumber
            const currency = (this.queryCurrencies.data() ?? []).find(
              d => d.id === this.castImportCellToString(n[importMap.currency])
            )
            const purchasePriceCell = n[importMap.purchasePriceWithoutVat]
            const purchasePrice =
              purchasePriceCell === undefined || purchasePriceCell === null || `${purchasePriceCell}`.trim() === ''
                ? NaN
                : Number(purchasePriceCell)
            // fall back to the product's purchase currency when the file has none
            const currencyId = currency?.id || productMatch.product.purchaseCurrency?.id
            // depoto-core does not strip undefined/NaN inside nested items, so omit unset fields entirely
            const oItem: Record<string, unknown> = {
              quantity: Number(n[importMap.quantity]),
              product: productMatch.product.id,
              ...(Number.isFinite(purchasePrice) ? { purchasePrice } : {}),
              ...(currencyId ? { currency: currencyId } : {}),
              note: n[importMap.itemNote] ? `${n[importMap.itemNote]}` : '',
            }
            purchaseOrders[rowNumber].items.push(oItem)
          } else {
            this.errors.push({
              ...errorContext,
              message: 'modal-purchase-orders-import.missing-product',
              params: { id: productMatch.identifier },
            })
            return
          }

          if (!purchaseOrders[rowNumber].externalReference) {
            purchaseOrders[rowNumber].externalReference = n[importMap.reference] ? `${n[importMap.reference]}` : ''
          }
          if (!purchaseOrders[rowNumber].dateEstimatedArrival) {
            purchaseOrders[rowNumber].dateEstimatedArrival = n[importMap.dateEstimatedArrival]
              ? this.fmtDatePipe.transform(`${n[importMap.dateEstimatedArrival]}`, 'yyyy-MM-dd')
              : ''
          }
          if (!purchaseOrders[rowNumber].depot) {
            purchaseOrders[rowNumber].depot = n[importMap.depot]
              ? (this.queryDepotsOptions.data() ?? []).find(d => d.name === `${n[importMap.depot]}`)?.id
              : undefined
          }
          if (!purchaseOrders[rowNumber].supplier?.id) {
            purchaseOrders[rowNumber].supplier = n[importMap.supplier]
              ? (this.querySuppliers.data() ?? []).find(s => s.name === `${n[importMap.supplier]}`)?.id
              : undefined
          }
          if (!purchaseOrders[rowNumber].note) {
            purchaseOrders[rowNumber].note = n[importMap.note] ? `${n[importMap.note]}` : ''
          }
          if (!purchaseOrders[rowNumber].privateNote) {
            purchaseOrders[rowNumber].privateNote = n[importMap.privateNote] ? `${n[importMap.privateNote]}` : ''
          }
          if (!purchaseOrders[rowNumber].itemNote) {
            purchaseOrders[rowNumber].itemNote = n[importMap.itemNote] ? `${n[importMap.itemNote]}` : ''
          }
          if (!purchaseOrders[rowNumber].createdBy) {
            purchaseOrders[rowNumber].created = new Date()
            purchaseOrders[rowNumber].createdBy = this.core.services.user.user
          }
        }
      }
      this.progress = (i + 1) / (data.length / 100)
    })

    Object.keys(purchaseOrders).forEach(k => {
      parsedPurchaseOrders.push(purchaseOrders[k])
    })
    if (!parsedPurchaseOrders?.length) this.progress = 100
    return parsedPurchaseOrders
  }

  checkSupplierIsValid(supplier: string): boolean {
    return (this.querySuppliers.data() ?? []).some(s => s.name === supplier)
  }

  checkDepotIsValid(depot: string): boolean {
    return (this.queryDepotsOptions.data() ?? []).some(d => d.name === depot)
  }
}
